import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { updateOrganization } from "../../../redux/organizationSlice";

const { Text, Title } = Typography;

const HCPCapacity = () => {
  const dispatch = useDispatch();
  const { organizationData, serviceTitanJobs } = useSelector(
    (state) => state?.organization
  );
  const [open, setOpen] = useState(false);
  const [capacity, setCapacity] = useState(
    structuredClone(organizationData?.hcpCapacityWindows) || []
  );
  const [capacityWindow, setCapacityWindow] = useState({
    serviceName: "",
    jobTypes: [],
    capacity: [
      { id: "monday", title: "Monday", windows: [] },
      { id: "tuesday", title: "Tuesday", windows: [] },
      { id: "wednesday", title: "Wednesday", windows: [] },
      { id: "thursday", title: "Thursday", windows: [] },
      { id: "friday", title: "Friday", windows: [] },
      { id: "saturday", title: "Saturday", windows: [] },
      { id: "sunday", title: "Sunday", windows: [] },
    ],
  });
  const [isEdit, setIsEdit] = useState(false);

  const handleAddCapacity = () => {
    if (capacityWindow?.serviceName && capacityWindow?.jobTypes?.length) {
      dispatch(
        updateOrganization({
          id: organizationData._id,
          payload: {
            hcpCapacityWindows: [...capacity, capacityWindow],
          },
        })
      );
     setCapacity([...capacity,capacityWindow])
      setOpen(false);
    }
  };

  const handleEditCapacity = () => {
    const updatedCapacity = capacity.map((item) =>
      item._id === capacityWindow._id ? { ...capacityWindow } : item
    );
    dispatch(
      updateOrganization({
        id: organizationData._id,
        payload: {
          hcpCapacityWindows: updatedCapacity,
        },
      })
    );
    setCapacity(updatedCapacity);
    setOpen(false);
    setIsEdit(false);
  };

  const handleDeleteCapacity = (item) => {
    const filteredCapacity = capacity.filter((e) => e._id !== item._id);
    dispatch(
      updateOrganization({
        id: organizationData._id,
        payload: {
          hcpCapacityWindows: filteredCapacity,
        },
      })
    );
  };

  const onAddNewCapacity = () => {
    setCapacityWindow({
      serviceName: "",
      jobTypes: [],
      capacity: [
        { id: "monday", title: "Monday", windows: [] },
        { id: "tuesday", title: "Tuesday", windows: [] },
        { id: "wednesday", title: "Wednesday", windows: [] },
        { id: "thursday", title: "Thursday", windows: [] },
        { id: "friday", title: "Friday", windows: [] },
        { id: "saturday", title: "Saturday", windows: [] },
        { id: "sunday", title: "Sunday", windows: [] },
      ],
    });
    setIsEdit(false);
    setOpen(true);
  };

  const columns = [
    {
      title: "Service Name",
      dataIndex: "serviceName",
      key: "serviceName",
      render: (serviceName) => (
        <Text strong>{serviceName}</Text>
      ),
    },
    {
      title: "Job Types",
      dataIndex: "jobTypes",
      key: "jobTypes",
      render: (jobTypes) => (
        <Flex gap="small" wrap="wrap">
          {jobTypes?.map((jobType) => {
            const job = serviceTitanJobs?.find((j) => j.id === jobType);
            return (
              <Tag key={jobType} color="blue">
                {job?.name || 'Unknown Job Type'}
              </Tag>
            );
          })}
        </Flex>
      ),
    },
    {
      title: "Capacity",
      dataIndex: "capacity",
      key: "capacity",
      render: (capacity, record) => (
        <AssociatedCapacity capacity={record.capacity} />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Flex vertical gap={25}>
          <Flex align="center" gap={25}>
            <EditOutlined
              style={{
                fontSize: "20px",
                color: "#1890ff",
                cursor: "pointer",
              }}
              onClick={() => {
                setCapacityWindow(record);
                setIsEdit(true);
                setOpen(true);
              }}
            />
            <Popconfirm
              title="Delete Capacity?"
              description="Are you sure to delete this Capacity?"
              placement="left"
              icon={<QuestionCircleOutlined style={{ color: "red" }} />}
              onConfirm={() => handleDeleteCapacity(record)}
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
            >
              <DeleteOutlined
                style={{
                  fontSize: "20px",
                  color: "red",
                  cursor: "pointer",
                }}
              />
            </Popconfirm>
          </Flex>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Spin spinning={serviceTitanJobs?.length === 0} fullscreen />
      <div className="flex items-center gap-4 mb-8">
        <CalendarOutlined style={{ fontSize: 20 }} />
        <Title level={3} className="text-black mb-0">
          HCP Capacity Management
        </Title>
      </div>
      <Flex style={{ margin: "10px 0px" }} vertical>
        <Flex justify="space-between" style={{ marginBottom: 10 }}>
          <Button
            onClick={onAddNewCapacity}
            style={{ background: "#1890ff", color: "#fff", borderColor: "#1890ff" }}
          >
            Add Capacity
          </Button>
        </Flex>
        <Table
          dataSource={capacity}
          columns={columns}
          rowKey={(record) => record._id}
          pagination={false}
          expandable={{
            expandedRowRender: (record) => <Divider />,
            rowExpandable: (record) => false,
          }}
        />
      </Flex>

      <Modal
        title="Add Capacity Window"
        open={open}
        width={"80%"}
        style={{ height: "calc(100vh - 300px)" }}
        okText="Save Capacity Window"
        onOk={isEdit ? handleEditCapacity : handleAddCapacity}
        okButtonProps={{
          disabled: !capacityWindow?.serviceName || !capacityWindow?.jobTypes?.length,
          style: { background: "#1890ff", borderColor: "#1890ff" },
        }}
        onCancel={() => {
          setOpen(false);
        }}
      >
        <Form layout="vertical">
          <Form.Item
            label="Service Name"
            required
            rules={[{ required: true, message: "Please enter service name" }]}
          >
            <Input
              value={capacityWindow.serviceName}
              onChange={(e) =>
                setCapacityWindow({ ...capacityWindow, serviceName: e.target.value })
              }
              placeholder="Enter service name"
            />
          </Form.Item>
          <Form.Item
            label="Job Types"
            required
            rules={[{ required: true, message: "Please select at least one job type" }]}
          >
            <Select
              mode="multiple"
              value={capacityWindow.jobTypes}
              onChange={(value) =>
                setCapacityWindow({ ...capacityWindow, jobTypes: value })
              }
              placeholder="Select job types"
              style={{ width: "100%" }}
            >
              {serviceTitanJobs?.map((job) => (
                <Select.Option key={job.id} value={job.id}>
                  {job.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Divider />
          <CapacityWindowForm
            capacity={capacityWindow.capacity}
            setCapacity={(newCapacity) =>
              setCapacityWindow({ ...capacityWindow, capacity: newCapacity })
            }
          />
        </Form>
      </Modal>
    </>
  );
};

const AssociatedCapacity = ({ capacity }) => {
  return (
    <>
      <Row gutter={[16, 16]}>
        {capacity.map((capacity) => (
          <Col span={8} key={capacity.id}>
            <div style={{ border: "1px solid #e8e8e8", padding: 10 }}>
              <Text strong style={{ color: "#1890ff" }}>
                {capacity.title}
              </Text>
              <div style={{ marginTop: 5 }}>
                {capacity.windows.length ? (
                  capacity.windows.map((win) => (
                    <div
                      key={win.id}
                      style={{ marginBottom: 5, display: "flex", justifyContent: "space-between" }}
                    >
                      <Text>
                        {win.start} - {win.end}
                      </Text>
                      <br />
                      <Text strong>{win.bookingSlots} Jobs</Text>
                    </div>
                  ))
                ) : (
                  <Text type="danger">No Jobs Available</Text>
                )}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );
};

const CapacityWindowForm = ({ capacity, setCapacity }) => {
  const handleAddWindow = (dayId) => {
    const newCapacity = capacity.map((day) => {
      if (day.id === dayId) {
        return {
          ...day,
          windows: [
            ...day.windows,
            {
              id: Date.now(),
              start: "09:00",
              end: "17:00",
              bookingSlots: 1,
            },
          ],
        };
      }
      return day;
    });
    setCapacity(newCapacity);
  };

  const handleRemoveWindow = (dayId, windowId) => {
    const newCapacity = capacity.map((day) => {
      if (day.id === dayId) {
        return {
          ...day,
          windows: day.windows.filter((win) => win.id !== windowId),
        };
      }
      return day;
    });
    setCapacity(newCapacity);
  };

  const handleWindowChange = (dayId, windowId, field, value) => {
    const newCapacity = capacity.map((day) => {
      if (day.id === dayId) {
        return {
          ...day,
          windows: day.windows.map((win) => {
            if (win.id === windowId) {
              return { ...win, [field]: value };
            }
            return win;
          }),
        };
      }
      return day;
    });
    setCapacity(newCapacity);
  };

  return (
    <div>
      {capacity.map((day) => (
        <Card
          key={day.id}
          title={day.title}
          style={{ marginBottom: 16 }}
          extra={
            <Button
              type="primary"
              onClick={() => handleAddWindow(day.id)}
              style={{ background: "#1890ff", borderColor: "#1890ff" }}
            >
              Add Window
            </Button>
          }
        >
          {day.windows.map((win) => (
            <div
              key={win.id}
              style={{
                border: "1px solid #e8e8e8",
                padding: 10,
                marginBottom: 10,
                display: "flex",
                gap: 10,
                alignItems: "center",
              }}
            >
              <Input
                type="time"
                value={win.start}
                onChange={(e) =>
                  handleWindowChange(day.id, win.id, "start", e.target.value)
                }
                style={{ width: 120 }}
              />
              <Text>to</Text>
              <Input
                type="time"
                value={win.end}
                onChange={(e) =>
                  handleWindowChange(day.id, win.id, "end", e.target.value)
                }
                style={{ width: 120 }}
              />
              <Input
                type="number"
                value={win.bookingSlots}
                onChange={(e) =>
                  handleWindowChange(day.id, win.id, "bookingSlots", parseInt(e.target.value))
                }
                style={{ width: 100 }}
                placeholder="Slots"
              />
              <Button
                type="text"
                danger
                onClick={() => handleRemoveWindow(day.id, win.id)}
              >
                Remove
              </Button>
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
};

export default HCPCapacity; 